import { useEffect, useState, useCallback } from 'react';
import type { Editor } from '@tiptap/react';
import type { Transaction } from '@tiptap/pm/state';
import type { Node } from '@tiptap/pm/model';

import type { TextStyleAttribute } from '../helpers/textStyleHelpers';
import { getParentNodeType, getNodeTypeFromSelection, createTextStyleHandler } from '../helpers/textStyleHelpers';

import type {
  AllTextDefaultStylesType,
  TableDefaultClassNamesEnum,
  TextDefaultSelectorsEnum,
} from '../../../grid/reduxStore/textDefaultStylingSlice.ts';

interface DefaultStylesProps {
  [TableDefaultClassNamesEnum.TABLE_BODY]: AllTextDefaultStylesType;
  [TableDefaultClassNamesEnum.TABLE_HEADER]: AllTextDefaultStylesType;
  [TableDefaultClassNamesEnum.TABLE_FOOTER]: AllTextDefaultStylesType;
  [TableDefaultClassNamesEnum.TABLE_SUBTOTAL]: AllTextDefaultStylesType;
  [TextDefaultSelectorsEnum.HEADING_1]: AllTextDefaultStylesType;
  [TextDefaultSelectorsEnum.HEADING_2]: AllTextDefaultStylesType;
  [TextDefaultSelectorsEnum.HEADING_3]: AllTextDefaultStylesType;
  [TextDefaultSelectorsEnum.HEADING_4]: AllTextDefaultStylesType;
  [TextDefaultSelectorsEnum.NORMAL]: AllTextDefaultStylesType;
}

export const useAttributeSync = (
  editor: Editor | null,
  defaultStyles: DefaultStylesProps,
  attribute: TextStyleAttribute,
  defaultValue: string
): readonly [string | undefined, (value: string | undefined) => void] => {
  const [selected, setSelected] = useState<string | undefined>(defaultValue);

  const getParentAttribute = useCallback(
    (parent: Node | null): string => {
      if (!parent) return defaultValue;

      const nodeType = getParentNodeType(parent);
      return defaultStyles?.[nodeType]?.[attribute] || defaultValue;
    },
    [defaultStyles, attribute, defaultValue]
  );

  const updateSelectionFromEditor = useCallback(() => {
    const nodeType = editor && getNodeTypeFromSelection(editor);
    const attributeValue = editor?.getAttributes('textStyle')?.[attribute];
    const currentDefault = (nodeType && defaultStyles?.[nodeType]?.[attribute]) || defaultValue;
    const spanDefault = defaultStyles?.p?.[attribute];

    const updateIfChanged = (value: string | undefined) => {
      if (selected !== value) {
        setSelected(value);
      }
    };

    if (nodeType === 'spanElement') {
      updateIfChanged(attributeValue || spanDefault);
    } else if (editor?.state.selection.empty) {
      if (!attributeValue && selected !== currentDefault && selected !== '') {
        updateIfChanged(currentDefault);
      } else if (attributeValue && selected !== attributeValue && selected !== '') {
        updateIfChanged(attributeValue);
      }
    }
  }, [editor, defaultStyles, attribute, defaultValue, selected]);

  useEffect(() => {
    const updateSelected = createTextStyleHandler({
      attributeName: attribute,
      getParentAttribute,
      parseValue: (value) => value.replace(/^["']|["']$/g, ''),
    });

    const handleUpdate = (event: { editor: Editor; transaction: Transaction }) => {
      const nodeType = getNodeTypeFromSelection(event.editor);
      const attrValue = event.editor?.getAttributes('textStyle')?.[attribute];

      if (nodeType === 'spanElement' && attrValue) {
        setSelected(attrValue);
        return;
      }

      updateSelected(event, setSelected);
    };

    editor?.on('selectionUpdate', handleUpdate);
    editor?.on('transaction', handleUpdate);
    return () => {
      editor?.off('selectionUpdate', handleUpdate);
      editor?.off('transaction', handleUpdate);
    };
  }, [editor, attribute, getParentAttribute]);

  useEffect(() => {
    if (!editor) return;
    updateSelectionFromEditor();
  }, [editor?.state.selection, updateSelectionFromEditor]);

  return [selected, setSelected];
};
